import React, { useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { SlidersHorizontal, X } from "lucide-react";
import { useCatalog } from "../lib/catalog";

const SORTS = [
  { v: "featured", label: "Featured" },
  { v: "low", label: "Price: Low to High" },
  { v: "high", label: "Price: High to Low" },
  { v: "rating", label: "Top Rated" },
  { v: "new", label: "New Arrivals" },
];

export default function Shop() {
  const { category } = useParams();
  const [params] = useSearchParams();
  const q = params.get("q")?.toLowerCase() || "";
  const [sort, setSort] = useState("featured");
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [showFilter, setShowFilter] = useState(false);

  const { products: allProducts, categories, loading } = useCatalog();

  const products = useMemo(() => {
    let list = category ? allProducts.filter((p) => p.category === category) : [...allProducts];
    if (q) {
      list = list.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.keyFeatures.join(" ").toLowerCase().includes(q),
      );
    }
    list = list.filter((p) => p.sale >= minPrice && p.sale <= maxPrice);
    if (sort === "low") list.sort((a, b) => a.sale - b.sale);
    if (sort === "high") list.sort((a, b) => b.sale - a.sale);
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    if (sort === "new") list.sort((a, b) => b.id - a.id);
    return list;
  }, [allProducts, category, q, sort, minPrice, maxPrice]);

  const activeCat = categories.find((c) => c.slug === category);

  return (
    <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-14" data-testid="shop-page">
      <div className="text-xs text-neutral-500 mb-3">
        <Link to="/" className="hover:text-neutral-900">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-neutral-900">Shop</Link>
        {activeCat && <>
          <span className="mx-2">/</span>
          <span className="text-neutral-900">{activeCat.name}</span>
        </>}
      </div>

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-display text-3xl md:text-5xl font-black leading-tight">
            {activeCat ? activeCat.name : "All Products"}
          </h1>
          <p className="text-neutral-500 mt-1 text-xs md:text-sm">
            {q ? `Results for "${q}" · ` : ""}{products.length} product{products.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="lg:hidden inline-flex items-center gap-2 h-10 px-4 rounded-full border border-neutral-300 text-sm"
            onClick={() => setShowFilter(true)}
            data-testid="open-filters-btn"
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="h-10 px-3 md:px-4 rounded-full border border-neutral-300 text-xs md:text-sm bg-white max-w-[160px] md:max-w-none"
            data-testid="sort-select"
          >
            {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-10 mt-6 md:mt-8">
        <aside className="hidden lg:block">
          <FilterPanel
            minPrice={minPrice} maxPrice={maxPrice}
            setMinPrice={setMinPrice} setMaxPrice={setMaxPrice}
            category={category} categories={categories}
          />
        </aside>

        <div>
          {loading ? (
            <div className="py-20 text-center text-neutral-500 animate-pulse">Loading products...</div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-neutral-500">No products match your filters.</p>
              <Link to="/shop" className="mt-4 inline-block text-[#FF5A1F] font-semibold">Clear and browse all</Link>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-7">
              {products.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      {showFilter && (
        <div className="fixed inset-0 z-[70] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowFilter(false)} />
          <aside className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-white p-6 overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-black text-xl">Filters</h3>
              <button onClick={() => setShowFilter(false)}><X className="w-5 h-5" /></button>
            </div>
            <FilterPanel
              minPrice={minPrice} maxPrice={maxPrice}
              setMinPrice={setMinPrice} setMaxPrice={setMaxPrice}
              category={category} categories={categories}
            />
            <button
              className="mt-6 w-full h-11 rounded-full bg-neutral-900 text-white font-semibold text-sm"
              onClick={() => setShowFilter(false)}
              data-testid="apply-filters-btn"
            >
              Apply
            </button>
          </aside>
        </div>
      )}
    </main>
  );
}

function FilterPanel({ minPrice, maxPrice, setMinPrice, setMaxPrice, category, categories }) {
  return (
    <div className="space-y-7">
      <div>
        <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 mb-3">Categories</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/shop" className={`hover:text-[#FF5A1F] ${!category ? "font-bold text-neutral-900" : ""}`}>All products</Link></li>
          {categories.map((c) => (
            <li key={c.slug}>
              <Link to={`/shop/${c.slug}`} className={`hover:text-[#FF5A1F] ${category === c.slug ? "font-bold text-neutral-900" : ""}`}>
                {c.name}
              </Link>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500 mb-3">Price</h4>
        <div className="flex items-center gap-2">
          <input type="number" value={minPrice} onChange={(e) => setMinPrice(Number(e.target.value) || 0)}
            className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm" placeholder="Min" data-testid="min-price-input" />
          <span className="text-neutral-400">–</span>
          <input type="number" value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value) || 0)}
            className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm" placeholder="Max" data-testid="max-price-input" />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {[[0, 200], [200, 300], [300, 500], [500, 1000]].map(([a, b]) => (
            <button key={`${a}-${b}`} onClick={() => { setMinPrice(a); setMaxPrice(b); }} className="text-xs px-3 py-1 rounded-full border border-neutral-300 hover:bg-neutral-100">
              ₹{a} – ₹{b}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
